import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '~/UIKit/Style';

import { useRequestCategories } from '~/requests/providerService';

import {
  InputSelect,
  InputText,
  ItemCreationInputsContainer,
  ItemCreationInputTemplate,
} from '~/ModularComponents/ItemCreationInputs';
import { useCheckProviderName } from '~/requests/providerService/useCheckProviderName';
import { useItemCreationContext } from '~/ModularComponents';
import { debounce } from 'lodash-es';
import { Typography } from '@mui/material';
import { ICON_ERROR } from '~/Constants/Colors.constants';
import { LocationEditCustomInputAddress } from '~/Screens/Settings/ProviderLocations/LocationEdit/LocationEditSections/LocationEditCustomElements';
import { ICityCountry } from '~/Models/DTO/CoreProvider.model';
import { useRequestCitiesCountries } from '~/requests';

const useStyles = makeStyles({
  errorMessage: {
    display: 'flex',
    padding: '10px 0',
    color: ICON_ERROR,
    justifyContent: 'end',
  },
});
export const CreateStudioSection = () => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const boundariesElement = useRef(null);

  const { values: createStudio } = useItemCreationContext();

  const { data: categories } = useRequestCategories();
  const { data: studioName, refetch, isFetching: isCheckNameLoading } = useCheckProviderName(
    createStudio.name,
  );

  const { data: countryList = [] } = useRequestCitiesCountries({});
  const countrySelections = countryList.map((country: ICityCountry) => ({
    label: country?.nameEnglish || '',
    value: country?.codeA2 || '',
    selected: false,
  }));

  const checkProviderName = useCallback(
    debounce((newName) => {
      refetch(newName);
    }, 1000),
    [],
  );

  useEffect(() => {
    if (createStudio.name) {
      checkProviderName(createStudio.name);
    }
  }, [createStudio.name]);

  return (
    <div ref={boundariesElement}>
      <ItemCreationInputsContainer title={t('settings.provider.studio.setup')} isHeaderVisible>
        <ItemCreationInputTemplate
          name="firstName"
          noIconBackground
          isLoading={isCheckNameLoading}
          InputElement={
            <InputText
              name="name"
              label={`${t('form.field.name')}*`}
              placeholder={t('form.field.name')}
              maxLength={50}
            />
          }
        />
        {studioName?.exist && createStudio.name.length !== 0 ? (
          <Typography className={classes.errorMessage}>
            {t('onboarding.studio.studioExistsErrorMessage')}
          </Typography>
        ) : null}
        <ItemCreationInputTemplate
          name="category"
          label={`${t('event.sportCategory')}*`}
          InputElement={
            <InputSelect
              name="category"
              label={`${t('event.sportCategory')}*`}
              options={categories}
              placeholder={t('event.selectCategory')}
            />
          }
        />
        <LocationEditCustomInputAddress countries={countrySelections} hideIcon />
        <ItemCreationInputTemplate
          name="countryName"
          noIconBackground
          InputElement={
            <InputText
              name="countryName"
              label={`${t('form.field.location.country')}*`}
              placeholder={t('form.field.location.country')}
              maxLength={50}
              readOnly
            />
          }
        />
        <ItemCreationInputTemplate
          name="city"
          noIconBackground
          InputElement={
            <InputText
              name="city"
              label={`${t('form.field.location.city')}*`}
              placeholder={t('form.field.location.city')}
              maxLength={50}
            />
          }
        />
        <ItemCreationInputTemplate
          name="street"
          noIconBackground
          InputElement={
            <InputText
              name="street"
              label={`${t('form.field.location.street')}*`}
              placeholder={t('form.field.location.street')}
              maxLength={50}
            />
          }
        />
        <ItemCreationInputTemplate
          name="zipCode"
          noIconBackground
          InputElement={
            <InputText
              name="zipCode"
              label={`${t('form.field.location.zip')}*`}
              placeholder={t('form.field.location.zip')}
              maxLength={50}
            />
          }
        />
      </ItemCreationInputsContainer>
    </div>
  );
};
